import { useState } from 'react';
import type { ClientePerfil } from '../../../../hooks/useAdminClientePerfil';
import PerfilEstilistico from '../../../clienteBackoffice/components/PerfilEstilistico';

interface HistorialChange {
  id: number;
  campo_modificado: string;
  valor_anterior: string | null;
  valor_nuevo: string | null;
  modificado_por: string;
  created_at: string;
}

interface Props {
  clienteId: number;
  previewPerfil: ClientePerfil;
  perfilChanges: HistorialChange[];
  prendasDb: Array<{ id: number | string; nombre: string; color: string }>;
  prendasCliente: Array<{ id: number | string; nombre: string; color: string }>;
  recDb: Array<{ id: number | string }>;
  recCliente: Array<{ id: number | string }>;
  looksDb: Array<{ id: number | string }>;
  looksCliente: Array<{ id: number | string }>;
}

export default function PerfilPreviewPanel({
  clienteId,
  previewPerfil,
  perfilChanges,
  prendasDb,
  prendasCliente,
  recDb,
  recCliente,
  looksDb,
  looksCliente,
}: Props) {
  const [adminTab, setAdminTab] = useState<'editar' | 'historial'>('editar');

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex items-center gap-1 bg-brand-gray-100 rounded-lg p-1">
        <button
          onClick={() => setAdminTab('editar')}
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md font-sans text-sm transition-all ${
            adminTab === 'editar'
              ? 'bg-brand-black text-white'
              : 'text-brand-gray-600 hover:text-brand-black'
          }`}
        >
          <i className="ri-eye-line" />
          Vista previa del cliente
        </button>
        <button
          onClick={() => setAdminTab('historial')}
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md font-sans text-sm transition-all ${
            adminTab === 'historial'
              ? 'bg-brand-black text-white'
              : 'text-brand-gray-600 hover:text-brand-black'
          }`}
        >
          <i className="ri-history-line" />
          Historial de cambios
          {perfilChanges.length > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold">
              {perfilChanges.length}
            </span>
          )}
        </button>
      </div>

      {adminTab === 'editar' ? (
        <div className="border border-brand-gray-200 rounded-2xl overflow-hidden bg-brand-cream">
          <div className="max-h-[80vh] overflow-y-auto p-4 md:p-6">
            <PerfilEstilistico
              clienteId={clienteId}
              prendas={prendasDb.length > 0 ? prendasDb : prendasCliente}
              recomendaciones={recDb.length > 0 ? recDb : recCliente}
              calendario={looksDb.length > 0 ? looksDb : looksCliente}
              perfilOverride={previewPerfil}
            />
          </div>
        </div>
      ) : (
        <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
          <div className="max-h-[80vh] overflow-y-auto p-4 md:p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black">
                  <i className="ri-history-line text-lg" />
                </div>
                <div>
                  <h3 className="font-serif text-brand-black text-lg font-medium">
                    Historial de cambios
                  </h3>
                  <p className="font-sans text-brand-gray-400 text-xs">
                    Registro de todas las modificaciones realizadas por Anthony
                  </p>
                </div>
              </div>

              {perfilChanges.length === 0 ? (
                <div className="text-center py-12 text-brand-gray-400">
                  <i className="ri-history-line text-3xl mb-2" />
                  <p className="font-sans text-sm">
                    Aún no hay cambios registrados para este cliente.
                  </p>
                  <p className="font-sans text-xs mt-1">
                    Los cambios se guardan automáticamente al pulsar "Guardar perfil".
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {perfilChanges.map((change) => (
                    <div key={change.id} className="border-l-2 border-brand-black pl-4 py-2">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-sans text-xs font-medium text-brand-black bg-brand-gray-100 px-2 py-0.5 rounded">
                          {change.campo_modificado}
                        </span>
                        <span className="font-sans text-[11px] text-brand-gray-400">
                          {new Date(change.created_at).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {change.valor_anterior !== null && change.valor_anterior !== '' && (
                          <p className="font-sans text-xs text-brand-gray-400 line-through">
                            Antes: {change.valor_anterior}
                          </p>
                        )}
                        <p className="font-sans text-xs text-brand-sage font-medium">
                          Ahora: {change.valor_nuevo ?? '—'}
                        </p>
                      </div>
                      <p className="font-sans text-[10px] text-brand-gray-400 mt-1">
                        por {change.modificado_por}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}